import { Injectable, inject } from '@angular/core';
import { ProductCart, OrderLineItem, calculateProductPoints } from '@models';
import { ProductCapabilitiesService, ProcessedProduct } from './product-capabilities.service';
import { AppleCareItem, ProductAppleCareService, UserService } from '@core';
import { EngravingUtilitiesService } from './engraving-utilities.service';
import { map, Observable } from 'rxjs';

export interface ProcessedOrderLineItem extends OrderLineItem {
  hasAppleCare: boolean;
  isEngravable: boolean;
  engravingText: string | null;
  appleCareItem: AppleCareItem | null;
}

@Injectable({
  providedIn: 'root'
})
export class ProductProcessingService {
  private capabilitiesService = inject(ProductCapabilitiesService);
  private engravingService = inject(EngravingUtilitiesService);
  private userService = inject(UserService);

  /**
   * Create processed products with line-based 1:1 AppleCare relationships
   */
  createProcessedProducts(products: ProductCart[]): ProcessedProduct[] {
    const appleCareMap = this.capabilitiesService.appleCarePresenceMap(); // Map<number, boolean>
    const engravableMap = this.capabilitiesService.engravableProductMap(); // Map<string, boolean>
    const appleCareItemsMap = this.capabilitiesService.appleCareItemsMap(); // Map<number, AppleCareItem>
    const engravingMap = this.engravingService.createEngravingDetailsMap(products as any[]);

    const immediateAppleCareState = this.createImmediateAppleCareState(products);

    return products.map((product) => {
      const productIdStr = product.productId.toString();
      const lineNumber = product.lineNumber;
      
      // Engravability by productId (same for all instances)
      const isEngravable = engravableMap.get(productIdStr) ?? false;
      
      // AppleCare by lineNumber (unique per cart line)
      const hasAppleCare = immediateAppleCareState.presenceMap.get(lineNumber) ?? 
                          appleCareMap.get(lineNumber) ?? false;
                          
      const appleCareItem = immediateAppleCareState.itemsMap.get(lineNumber) ?? 
                           appleCareItemsMap.get(lineNumber) ?? null;

      return {
        ...product,
        hasAppleCare,
        isEngravable,
        engravingText: engravingMap.get(productIdStr) ?? null,
        appleCareItem,
      };
    });
  }

  /**
   * Create processed order line items with 1:1 AppleCare
   */


  /**
   * Filter out AppleCare products
   */
  filterOutAppleCareProducts(products: ProcessedProduct[]): Observable<ProcessedProduct[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      map((appleCareIds) => {
        return products.filter(
          (product) => !appleCareIds.includes(product.productId)
        );
      })
    );
  }

  filterOutAppleCareOrderLineItems(lineItems: ProcessedOrderLineItem[]): Observable<ProcessedOrderLineItem[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      map((appleCareIds) => {
        return lineItems.filter(
          (lineItem) => !appleCareIds.includes(lineItem.productId)
        );
      })
    );
  }

  filterOutAppleCareProductsBaseProduct(products: ProductCart[]): Observable<ProductCart[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      map((appleCareIds) => {
        return products.filter(
          (product) => !appleCareIds.includes(product.productId)
        );
      })
    );
  }

  filterOutAppleCareOrderLineItemsBase(lineItems: OrderLineItem[]): Observable<OrderLineItem[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      map((appleCareIds) => {
        return lineItems.filter(
          (lineItem) => !appleCareIds.includes(lineItem.productId)
        );
      })
    );
  }

  /**
   * Create immediate 1:1 AppleCare state from cart products using lineNumber
   */
  private createImmediateAppleCareState(products: ProductCart[]): {
    presenceMap: Map<number, boolean>;
    itemsMap: Map<number, AppleCareItem>;
  } {
    const presenceMap = new Map<number, boolean>();
    const itemsMap = new Map<number, AppleCareItem>();
    
    const appleCareIds = this.capabilitiesService.getAppleCareProductIdsSync();
    
    // Initialize all lines as not having AppleCare
    products.forEach(product => {
      presenceMap.set(product.lineNumber, false);
    });
    
    // Find AppleCare products and their 1:1 relationships
    const appleCareProducts = products.filter(product => 
      appleCareIds.includes(product.productId)
    );
    
    appleCareProducts.forEach(appleCareProduct => {
      // Primary: Use relatedLineNumber for 1:1 relationship
      if (appleCareProduct.relatedLineNumber !== null && 
          appleCareProduct.relatedLineNumber !== undefined) {
        
        const parentProduct = products.find(p => 
          p.lineNumber === appleCareProduct.relatedLineNumber &&
          !appleCareIds.includes(p.productId)
        );
        
        if (parentProduct) {
          presenceMap.set(parentProduct.lineNumber, true);
          
          // Use parent product's pointConversionRate for AppleCare (inherits accelerator)
          const pointsCalc = calculateProductPoints(
            appleCareProduct.price,
            parentProduct.pointConversionRate ?? undefined,
            this.userService.conversionRate()
          );
          
          const appleCareItem: AppleCareItem = {
            id: appleCareProduct.productId.toString(),
            productName: appleCareProduct.name,
            price: appleCareProduct.price,
            points: pointsCalc.newPoints,
            oldPoints: pointsCalc.oldPoints ?? undefined,
            isAdded: true,
            brand: appleCareProduct.brandName || "Apple",
            model: parentProduct.name,
            productId: appleCareProduct.productId,
            lineNumber: appleCareProduct.lineNumber,
          };
          
          itemsMap.set(parentProduct.lineNumber, appleCareItem);
        }
      } else {
        // Fallback: Use relatedParentProducts
        const parentIds = appleCareProduct.relatedParentProducts ?? [];
        
        parentIds.forEach(parent => {
          const parentProduct = products.find(p => p.productId === parent.productId);
          
          if (parentProduct) {
            presenceMap.set(parentProduct.lineNumber, true);
            
            // Use parent product's pointConversionRate for AppleCare (inherits accelerator)
            const pointsCalc = calculateProductPoints(
              appleCareProduct.price,
              parentProduct.pointConversionRate ?? undefined,
              this.userService.conversionRate()
            );
            
            const appleCareItem: AppleCareItem = {
              id: appleCareProduct.productId.toString(),
              productName: appleCareProduct.name,
              price: appleCareProduct.price,
              points: pointsCalc.newPoints,
              oldPoints: pointsCalc.oldPoints ?? undefined,
              isAdded: true,
              brand: appleCareProduct.brandName || "Apple",
              model: appleCareProduct.name.replace(/AppleCare\+?\s*(for\s*)?/i, "").trim(),
              productId: appleCareProduct.productId,
              lineNumber: appleCareProduct.lineNumber,
            };
            
            itemsMap.set(parentProduct.lineNumber, appleCareItem);
          }
        });
      }
    });
    
    return { presenceMap, itemsMap };
  }

  /**
   * Helper: Find parent product for AppleCare via relatedLineNumber
   */
  findParentForAppleCare(appleCareProduct: ProductCart, allProducts: ProductCart[]): ProductCart | null {
    if (appleCareProduct.relatedLineNumber === null || 
        appleCareProduct.relatedLineNumber === undefined) {
      return null;
    }
    
    return allProducts.find(p => p.lineNumber === appleCareProduct.relatedLineNumber) ?? null;
  }

  /**
   * Check if a line has AppleCare (1:1 check)
   */
  lineHasAppleCare(lineNumber: number, products: ProductCart[]): boolean {
    const appleCareIds = this.capabilitiesService.getAppleCareProductIdsSync();
    
    return products.some(product => 
      appleCareIds.includes(product.productId) &&
      product.relatedLineNumber === lineNumber
    );
  }

  /**
   * Get the single AppleCare product for a line (1:1 relationship)
   */
  getAppleCareForLine(lineNumber: number, products: ProductCart[]): ProductCart | null {
    const appleCareIds = this.capabilitiesService.getAppleCareProductIdsSync();
    
    return products.find(product => 
      appleCareIds.includes(product.productId) &&
      product.relatedLineNumber === lineNumber
    ) ?? null;
  }
}